/**
 * Batch Loading Utilities
 * 
 * Handles loading campaigns in batches to avoid rate limiting.
 */

// Batch loading config - prevents hitting rate limits
const BATCH_SIZE = 3; // Load 3 at a time
const BATCH_DELAY_MS = 150; // 150ms between batches

/**
 * Load campaigns in batches to avoid rate limiting
 * 
 * Key safeguards:
 * 1. Skips campaigns that already have tasks loaded (no API call needed)
 * 2. Loads in small batches with delays to respect rate limits
 * 3. Handles errors gracefully per-campaign
 * 
 * @param {string[]} campaignIds - IDs to load
 * @param {Object[]} sprints - Current sprint data
 * @param {Function} loadFullSprint - Loader function
 * @returns {Promise<Object[]>} Successfully loaded campaigns
 */
export async function loadCampaignsInBatches(campaignIds, sprints, loadFullSprint) {
  const results = [];
  
  // First, separate already-loaded from needs-loading
  const alreadyLoaded = [];
  const needsLoading = [];
  
  for (const campaignId of campaignIds) {
    const campaign = sprints.find(s => s.id === campaignId);
    if (!campaign) continue;
    
    // Check if tasks array exists (even empty array means it's loaded)
    if (Array.isArray(campaign.tasks)) {
      alreadyLoaded.push(campaign);
    } else {
      needsLoading.push(campaignId);
    }
  }
  
  console.log(`[MultiCampaignLoader] ${alreadyLoaded.length} already loaded, ${needsLoading.length} need loading`);
  
  results.push(...alreadyLoaded);
  
  // If nothing needs loading, we're done (no API calls!)
  if (needsLoading.length === 0) {
    return results;
  }
  
  // Load remaining in batches
  const totalBatches = Math.ceil(needsLoading.length / BATCH_SIZE);
  for (let i = 0; i < needsLoading.length; i += BATCH_SIZE) {
    const batchNum = Math.floor(i / BATCH_SIZE) + 1;
    const batch = needsLoading.slice(i, i + BATCH_SIZE);
    console.log(`[MultiCampaignLoader] Loading batch ${batchNum}/${totalBatches}: ${batch.length} campaigns`);
    
    // Load batch in parallel
    const batchResults = await Promise.all(
      batch.map(async (campaignId) => {
        try {
          return await loadFullSprint(campaignId);
        } catch (error) {
          console.warn(`[MultiCampaignLoader] Failed to load campaign ${campaignId}:`, error.message);
          return null;
        }
      })
    );
    
    // Add successful loads
    batchResults.forEach(campaign => {
      if (campaign) results.push(campaign);
    });
    
    // Delay before next batch (if there is one)
    if (i + BATCH_SIZE < needsLoading.length) {
      await new Promise(resolve => setTimeout(resolve, BATCH_DELAY_MS));
    }
  }
  
  return results;
}
